import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import "../assets/css/biodata.css";

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction
  ? "https://schools.luth.gov.ng" || "https://10.0.0.57"
  : "http://localhost:3003";

const StudentBioDataForm = ({ initialData = null, isEditMode = false }) => {
  const { matricNo: urlMatric } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: "",
    dob: "",
    gender: "",
    maritalStatus: "",
    email: "",
    phone: "",
    address: "",
    matricNo: "",
    department: "",
    sponsorName: "",
    sponsorPhone: "",
    sponsorPhone2: "",
    sponsorEmail: "",
    sponsorAddress: "",
    nextOfKin: "",
    nextOfKinPhone: "",
    nextOfKinAddress: "",
    nextOfKinRelationship: "",
    nextOfKinEmail: "",
    nationality: "",
    stateOfOrigin: "",
    lga: "",
    religion: "",
    jambRegNo: "",
    primarySchool: "",
    primaryGradYear: "",
    primaryQualification: "",
    secondarySchool: "",
    secondaryGradYear: "",
    secondaryQualification: "",
    postSecondarySchool: "",
    postSecondaryGradYear: "",
    postSecondaryQualification: "",
    otherQualification: "",
    otherQualificationYear: "",
    otherQualificationSchool: "",
    // picture will hold either a File (when user selects new file) OR a string URL (existing)
    picture: null,
    entryMode: "UTME",
    entryYear: "N/A",
    level: "N/A",
  });

  const [originalPictureUrl, setOriginalPictureUrl] = useState(null); // keep original URL separate
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ref to hidden file input so "Change" button can trigger it
  const fileInputRef = useRef(null);

  // date helpers
  const formatDateForInput = (value) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const dt = new Date(value);
    if (isNaN(dt)) return "";
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  const formatDateForDisplay = (value) => {
    if (!value) return "Not set";
    const dt = new Date(value);
    if (isNaN(dt)) return value;
    return dt.toLocaleDateString();
  };

  // initial fetch for user basic info
  useEffect(() => {
    async function fetchUser() {
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/api/users/${urlMatric}`,
        );
        setFormData((prev) => ({
          ...prev,
          fullName: data.fullName || prev.fullName,
          email: data.email || prev.email,
          matricNo: data.matricNo || prev.matricNo,
          department: data.programme || prev.department,
        }));
      } catch (err) {
        console.error(err);
        setError("Failed to load profile data.");
      } finally {
        setLoading(false);
      }
    }
    fetchUser();
  }, [urlMatric]);

  // populate full biodata from initialData or API when editing
  useEffect(() => {
    if (initialData) {
      const pictureVal = initialData.picture || null;
      setFormData((prev) => ({
        ...prev,
        fullName: initialData.fullName || prev.fullName,
        dob: initialData.dateOfBirth || prev.dob,
        gender: initialData.gender || prev.gender,
        maritalStatus: initialData.maritalStatus || prev.maritalStatus,
        email: initialData.email || prev.email,
        phone: initialData.phoneNumber || prev.phone,
        address: initialData.address || prev.address,
        matricNo: initialData.matricNumber || prev.matricNo,
        department: initialData.department || prev.department,
        sponsorName: initialData.sponsorName || prev.sponsorName,
        sponsorPhone: initialData.sponsorPhone1 || prev.sponsorPhone,
        sponsorPhone2: initialData.sponsorPhone2 || prev.sponsorPhone2,
        sponsorEmail: initialData.sponsorEmail || prev.sponsorEmail,
        sponsorAddress: initialData.sponsorAddress || prev.sponsorAddress,
        nextOfKin: initialData.nextOfKinName || prev.nextOfKin,
        nextOfKinPhone: initialData.nextOfKinPhone || prev.nextOfKinPhone,
        nextOfKinAddress: initialData.nextOfKinAddress || prev.nextOfKinAddress,
        nextOfKinRelationship:
          initialData.nextOfKinRelationship || prev.nextOfKinRelationship,
        nextOfKinEmail: initialData.nextOfKinEmail || prev.nextOfKinEmail,
        nationality: initialData.nationality || prev.nationality,
        stateOfOrigin: initialData.stateOfOrigin || prev.stateOfOrigin,
        lga: initialData.lga || prev.lga,
        religion: initialData.religion || prev.religion,
        jambRegNo: initialData.jambRegNumber || prev.jambRegNo,
        primarySchool: initialData.primarySchool || prev.primarySchool,
        primaryGradYear: initialData.primaryGradYear || prev.primaryGradYear,
        primaryQualification:
          initialData.primaryQualification || prev.primaryQualification,
        secondarySchool: initialData.secondarySchool || prev.secondarySchool,
        secondaryGradYear:
          initialData.secondaryGradYear || prev.secondaryGradYear,
        secondaryQualification:
          initialData.secondaryQualification || prev.secondaryQualification,
        postSecondarySchool:
          initialData.postSecondarySchool || prev.postSecondarySchool,
        postSecondaryGradYear:
          initialData.postSecondaryGradYear || prev.postSecondaryGradYear,
        postSecondaryQualification:
          initialData.postSecondaryQualification ||
          prev.postSecondaryQualification,
        otherQualification:
          initialData.otherQualification || prev.otherQualification,
        otherQualificationYear:
          initialData.otherQualificationsYear || prev.otherQualificationsYear,
        picture: pictureVal, // may be a URL string
        entryMode: initialData.entryMode || prev.entryMode,
        entryYear: initialData.entryYear || prev.entryYear,
        level: initialData.level || prev.level,
      }));
      if (pictureVal) setOriginalPictureUrl(pictureVal);
      setLoading(false);
    } else if (isEditMode) {
      // if editing but no initialData given, fetch biodata endpoint
      axios
        .get(`${API_BASE_URL}/api/biodata/${urlMatric}`)
        .then(({ data }) => {
          const pictureVal = data.picture || null;
          setFormData((prev) => ({ ...prev, ...data, picture: pictureVal }));
          if (pictureVal) setOriginalPictureUrl(pictureVal);
        })
        .catch(() => setError("Failed to load existing biodata."))
        .finally(() => setLoading(false));
    }
  }, [initialData, isEditMode, urlMatric]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // file selection (new passport)
  const handleFileChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    // set the picture to the File object; preview will use object URL
    setFormData((prev) => ({ ...prev, picture: file }));
  };

  // trigger the hidden file input
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  // discard the chosen file and revert to original (URL)
  const revertToOriginalPicture = () => {
    setFormData((prev) => ({ ...prev, picture: originalPictureUrl || null }));
    // clear file input value so same file can be chosen again if desired
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();

    // Append other fields
    Object.entries(formData).forEach(([k, v]) => {
      if (k === "picture") return; // handle below
      form.append(k, v ?? "");
    });

    // Picture handling:
    // - If user selected a File (instanceof File), append as 'picture'
    // - Else if picture is a URL string (original), append as 'pictureUrl' so backend can keep it
    if (formData.picture instanceof File) {
      form.append("picture", formData.picture);
    } else if (typeof formData.picture === "string" && formData.picture) {
      form.append("pictureUrl", formData.picture);
    } else {
      // no picture chosen and no original
      // you can choose to append nothing or append empty string
      form.append("pictureUrl", "");
    }

    try {
      const url = isEditMode
        ? `${API_BASE_URL}/api/biodata/${formData.matricNo}`
        : `${API_BASE_URL}/api/biodata`;
      const method = isEditMode ? "put" : "post";
      await axios({
        url,
        method,
        data: form,
        headers: { "Content-Type": "multipart/form-data" },
      });
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      setError("Submission failed. Please try again.");
    }
  };

  if (loading) return <p>Loading profile…</p>;

  // Determine preview src:
  // - If formData.picture is a File => use object URL
  // - If it's a string URL => use it
  // - Else null
  let previewSrc = null;
  if (formData.picture instanceof File) {
    try {
      previewSrc = URL.createObjectURL(formData.picture);
    } catch (e) {
      previewSrc = null;
    }
  } else if (typeof formData.picture === "string" && formData.picture) {
    previewSrc = formData.picture;
  }

  return (
    <div className="bio-form-container">
      <h2>{isEditMode ? "Edit Bio-data" : "Student Bio-data Form"}</h2>
      {error && <p className="error-message">{error}</p>}
      <form onSubmit={handleSubmit}>
        {/* Academic Details */}
        <FormSection title="Academic Details">
          <Input
            label="Matric Number"
            name="matricNo"
            value={formData.matricNo}
            onChange={handleChange}
            readOnly
          />
          <Input
            label="Level"
            name="level"
            value={formData.level}
            onChange={handleChange}
            readOnly
          />
          <Input
            label="Entry Mode"
            name="entryMode"
            value={formData.entryMode}
            onChange={handleChange}
            readOnly
          />
          <Input
            label="Entry Year"
            name="entryYear"
            value={formData.entryYear}
            onChange={handleChange}
            readOnly
          />
        </FormSection>

        {/* Personal Information */}
        <FormSection title="Personal Information">
          <Input
            label="Full Name"
            name="fullName"
            value={formData.fullName}
            readOnly
          />

          <DOBField
            label="Date of Birth"
            name="dob"
            value={formData.dob}
            onChange={(newVal) => setFormData((p) => ({ ...p, dob: newVal }))}
            formatDateForDisplay={formatDateForDisplay}
            formatDateForInput={formatDateForInput}
          />

          <Select
            label="Gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            options={["Male", "Female"]}
          />
          <Select
            label="Marital Status"
            name="maritalStatus"
            value={formData.maritalStatus}
            onChange={handleChange}
            options={["Single", "Married"]}
          />
          <Input
            label="Nationality"
            name="nationality"
            value={formData.nationality}
            onChange={handleChange}
          />
          <Input
            label="State of Origin"
            name="stateOfOrigin"
            value={formData.stateOfOrigin}
            onChange={handleChange}
          />
          <Input
            label="LGA"
            name="lga"
            value={formData.lga}
            onChange={handleChange}
          />
          <Input
            label="Religion"
            name="religion"
            value={formData.religion}
            onChange={handleChange}
          />
        </FormSection>

        {/* Contact */}
        <FormSection title="Contact Information">
          <Input
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            readOnly
          />
          <Input
            label="Phone Number"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
          />
          <Input
            label="Home Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
          />
        </FormSection>

        {/* Academic Info */}
        <FormSection title="Academic Information">
          <Input
            label="Department"
            name="department"
            value={formData.department}
            onChange={handleChange}
            readOnly
          />
          <Input
            label="JAMB Registration Number"
            name="jambRegNo"
            value={formData.jambRegNo}
            onChange={handleChange}
          />
        </FormSection>

        {/* Sponsor */}
        <FormSection title="Sponsor Information">
          <Input
            label="Sponsor Name"
            name="sponsorName"
            value={formData.sponsorName}
            onChange={handleChange}
          />
          <Input
            label="Sponsor Phone 1"
            name="sponsorPhone"
            type="tel"
            value={formData.sponsorPhone}
            onChange={handleChange}
          />
          <Input
            label="Sponsor Phone 2"
            name="sponsorPhone2"
            type="tel"
            value={formData.sponsorPhone2}
            onChange={handleChange}
          />
          <Input
            label="Sponsor Email"
            name="sponsorEmail"
            type="email"
            value={formData.sponsorEmail}
            onChange={handleChange}
          />
          <Input
            label="Sponsor Address"
            name="sponsorAddress"
            value={formData.sponsorAddress}
            onChange={handleChange}
          />
        </FormSection>

        {/* Next of Kin */}
        <FormSection title="Next of Kin">
          <Input
            label="Name"
            name="nextOfKin"
            value={formData.nextOfKin}
            onChange={handleChange}
          />
          <Input
            label="Phone"
            name="nextOfKinPhone"
            type="tel"
            value={formData.nextOfKinPhone}
            onChange={handleChange}
          />
          <Input
            label="Address"
            name="nextOfKinAddress"
            value={formData.nextOfKinAddress}
            onChange={handleChange}
          />
          <Input
            label="Email"
            name="nextOfKinEmail"
            type="email"
            value={formData.nextOfKinEmail}
            onChange={handleChange}
          />
          <Input
            label="Relationship"
            name="nextOfKinRelationship"
            value={formData.nextOfKinRelationship}
            onChange={handleChange}
          />
        </FormSection>

        {/* Education */}
        <FormSection title="Educational Background">
          <Input
            label="Primary School"
            name="primarySchool"
            value={formData.primarySchool}
            onChange={handleChange}
          />
          <Input
            label="Graduation Year"
            name="primaryGradYear"
            type="number"
            value={formData.primaryGradYear}
            onChange={handleChange}
          />
          <Input
            label="Qualification"
            name="primaryQualification"
            value={formData.primaryQualification}
            onChange={handleChange}
          />
          <Input
            label="Secondary School"
            name="secondarySchool"
            value={formData.secondarySchool}
            onChange={handleChange}
          />
          <Input
            label="Graduation Year"
            name="secondaryGradYear"
            type="number"
            value={formData.secondaryGradYear}
            onChange={handleChange}
          />
          <Input
            label="Qualification"
            name="secondaryQualification"
            value={formData.secondaryQualification}
            onChange={handleChange}
          />
          <Input
            label="Post-Secondary School"
            name="postSecondarySchool"
            value={formData.postSecondarySchool}
            onChange={handleChange}
          />
          <Input
            label="Graduation Year"
            name="postSecondaryGradYear"
            type="number"
            value={formData.postSecondaryGradYear}
            onChange={handleChange}
          />
          <Input
            label="Qualification"
            name="postSecondaryQualification"
            value={formData.postSecondaryQualification}
            onChange={handleChange}
          />
          <Input
            label="Other Qualification"
            name="otherQualification"
            value={formData.otherQualification}
            onChange={handleChange}
          />
          <Input
            label="Year"
            name="otherQualificationYear"
            type="number"
            value={formData.otherQualificationYear}
            onChange={handleChange}
          />
          <Input
            label="Institution"
            name="otherQualificationSchool"
            value={formData.otherQualificationSchool}
            onChange={handleChange}
          />
        </FormSection>

        {/* Upload / Show Passport */}
        <FormSection title="Upload Picture">
          <div className="input-group passport-group">
            <label>Passport Photo</label>

            {/* Preview area (original URL or selected file) */}
            <div
              className="passport-preview"
              style={{ display: "flex", gap: 12, alignItems: "center" }}
            >
              {previewSrc ? (
                <img
                  src={previewSrc}
                  alt="Passport preview"
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: "cover",
                    borderRadius: 6,
                    border: "1px solid #ddd",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 120,
                    height: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px dashed #ccc",
                    borderRadius: 6,
                    color: "#666",
                    fontSize: 12,
                  }}
                >
                  No passport
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    onClick={openFilePicker}
                    className="change-button"
                  >
                    Change
                  </button>

                  {/* Show 'Revert' button only when there is an original URL and user has selected a different file */}
                  {originalPictureUrl && formData.picture instanceof File && (
                    <button
                      type="button"
                      onClick={revertToOriginalPicture}
                      className="revert-button"
                    >
                      Revert to original
                    </button>
                  )}
                </div>

                {/* Show current info about what's shown */}
                <small>
                  {formData.picture instanceof File
                    ? `New file: ${formData.picture.name}`
                    : originalPictureUrl
                      ? "Click Change to select a new passport"
                      : "No passport on record"}
                </small>
              </div>
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              id="picture"
              type="file"
              name="picture"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
              // required only if no original picture and no selected file
              required={
                !(originalPictureUrl || formData.picture instanceof File)
              }
            />
            <small className="hint">
              Allowed formats: jpg, png. Max 1MB (backend dependent).
            </small>
          </div>
        </FormSection>

        <button type="submit" className="submit-button">
          Submit
        </button>
      </form>
    </div>
  );
};

/* ---------- Reusable components below ---------- */

const FormSection = ({ title, children }) => (
  <div className="form-section">
    <h3>{title}</h3>
    <div className="form-grid">{children}</div>
  </div>
);

const Input = ({ label, name, type = "text", ...rest }) => (
  <div className="input-group">
    <label htmlFor={name}>{label}</label>
    <input id={name} name={name} type={type} {...rest} />
  </div>
);

const Select = ({ label, name, options = [], ...rest }) => (
  <div className="input-group">
    <label htmlFor={name}>{label}</label>
    <select id={name} name={name} {...rest}>
      <option value="">Select</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);

/* DOBField: display + change like before */
const DOBField = ({
  label,
  name,
  value,
  onChange,
  formatDateForDisplay,
  formatDateForInput,
}) => {
  const [editing, setEditing] = useState(false);
  const [tempDate, setTempDate] = useState(() => formatDateForInput(value));

  useEffect(() => {
    setTempDate(formatDateForInput(value));
  }, [value, formatDateForInput]);

  const startEdit = () => {
    setTempDate(formatDateForInput(value));
    setEditing(true);
  };
  const cancelEdit = () => {
    setTempDate(formatDateForInput(value));
    setEditing(false);
  };
  const saveEdit = () => {
    onChange(tempDate || "");
    setEditing(false);
  };

  return (
    <div className="input-group dob-group">
      <label htmlFor={name}>{label}</label>
      {!editing ? (
        <div
          className="dob-display"
          style={{ display: "flex", gap: 12, alignItems: "center" }}
        >
          <span id={name} aria-live="polite">
            {value ? formatDateForDisplay(value) : "Not set"}
          </span>
          <button type="button" onClick={startEdit} className="link-button">
            Change
          </button>
        </div>
      ) : (
        <div
          className="dob-edit"
          style={{ display: "flex", gap: 8, alignItems: "center" }}
        >
          <input
            id={name}
            name={name}
            type="date"
            value={tempDate}
            onChange={(e) => setTempDate(e.target.value)}
          />
          <button type="button" onClick={saveEdit} className="save-button">
            Save
          </button>
          <button type="button" onClick={cancelEdit} className="cancel-button">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default StudentBioDataForm;
